/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContent.js
 * @brief  This file is responsible for general tab functionality
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useRef } from "react";
import FilterCategory from "./FilterCategory";
import FilterLocationSearch from "../FilterLocationSearch";
import FilterKeywordSearch from "../FilterKeywordSearch";
import Accordion from "react-bootstrap/Accordion";
import { filterContext } from "../../../../pages/v2/Home";
import {
  accsPvtBthRmchkBxFilter,
  adaAccessibleChkBx,
  alwPetsChkBx,
  BUY,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
  DEFAULT_STEP_VALUE_BUY,
  DEFAULT_STEP_VALUE_LTR,
  DEFAULT_STEP_VALUE_STR,
  INDEX_TO_BE_SLICE_15,
  LONG_RENT,
  pvtEntrnceChkBx,
} from "../../../../constants/common";
import { getPropertyTypes } from "../../../../services/commonServices";
import { getSquareFeetList } from "../../../../services/wantAdService";
import { handleRadioButonChange } from "../../../../actions/common";
import RangeSliderComponent from "../../../Common/RangeSlider";
import SelectDropDown from "../../../Common/SelectDropDown";
import {
  convertToNumber,
  handleNegativeInput,
} from "../../../../helpers/validations";
import TextBox from "../../../Common/TextBox";
import {
  replaceComma,
  getLocalStorage,
  replaceString,
  thousandSeperatedValue,
  thousandSeperatedValueFilter,
  getPriceText,
  checkIsNumber,
  arraySplice,
  filterArrayBasedOnKey,
} from "../../../../helpers/common";
import DateRangeSelector from "../../../DateRangeSelector";
import PremiumPlanModal from "../../../../components/PlanUpgrade/PremiumPlanModal";
import { useUserDetails } from "@/hooks";
import { formatThousandSeparatedValue } from "@/helpers";


const LeftpanelTabContent = (props) => {
  const {
    updateFilter,
    filterQuery,
    setUserLocation,
    gitchaNotAvailble,
    checkGitchaAvailableInLoc,
    userLocation,
    additionalLocations,
    removeLocationAt,
  } = props;

  const {
    userDetails: userDetailsFromHook,
    loadUserData,
  } = useUserDetails();

  // Selected cities render as removable chips (see FilterLocationSearch),
  // so the input itself always starts empty/typing-only rather than
  // mirroring the primary location's formatted address.
  const [location, setLocation] = useState("");
  const [propertyList, setPropertyList] = useState([]);
  const [typeOfPlaceList, setTypeOfPlaceList] = useState([]);
  const [selectedTypeOfPlace, setSelectedTypeOfPlace] = useState([]);
  const [budgetRange, setBudgetRange] = useState("");
  const [minError, setMinError] = useState("");
  const [maxError, setMaxError] = useState("");

  const getSelectedItems = (property) => {
    const selectedItemArray = _.get(filterQuery, property, []);
    return selectedItemArray;
  };

  const [searchKeyword, setSearchKeyword] = useState(filterQuery?.search || "");
  const [squareFootageMinValue, setSquareFootageMinValue] = useState(null);
  const [squareFootageMaxValue, setSquareFootageMaxValue] = useState(null);
  const filterCntxt = useContext(filterContext);
  const [propertyCount, setPropertyCount] = useState(filterQuery?.propertyType?.length || 0);
  const [typeOfPlaceCount, setTPCount] = useState(0);
  const { adType, clearFilterQuery } = filterCntxt;
  const [isPrivateRoomSelected, setIsPrivateRoomSelected] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [squareFeetMinList, setSqareFeetMinList] = useState([]);
  const [squareFeetMaxList, setSqareFeetMaxList] = useState([]);
  const [isAgentOnly, setIsAgentOnly] = useState(false);
  const isFirstMount = useRef(true);
  const isFirstSqftLoad = useRef(true);

  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const MIN_SF_DROPDOWN = "minSFDrop";
  const MAX_SF_DROPDOWN = "maxSFDrop";
  const minRef = useRef(null);
  const maxRef = useRef(null);
  let DEFAULT_MAX_BUDGET;
  const DEFAULT_MIN_BUDGET = 0;
  let DEFAULT_STEP_VALUE;

  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_STR;
  }

  const budgetMin = _.get(filterQuery, "priceMin");
  const budgetMax = _.get(filterQuery, "priceMax");
  const STR_OPTION_DETAILS_PVT_ROOM = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
    { id: accsPvtBthRmchkBxFilter, text: "Access to private bathroom" },
    { id: pvtEntrnceChkBx, text: "Private Entrance" },
  ];

  const STR_OPTION_DETAILS_ALL = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
  ];

  const formatSqFtLabel = (val, originalLabel = "") => {
    if (val === null || val === undefined) return originalLabel;
    const hasPlus = String(originalLabel).includes("+");
    return formatThousandSeparatedValue(Number(val)) + (hasPlus ? "+" : "");
  };

  useEffect(() => {
    setIsAgentOnly(false);
  }, [adType]);

  useEffect(() => {
    if (adType === BUY) {
      getPropertyTypes(adType)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      setPropertyList([]);
    }

    let DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET];
    if (budgetMin && !budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, DEFAULT_MAX_BUDGET];
    } else if (!budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, budgetMax];
    } else if (budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, budgetMax];
    }
    setBudgetRange(DEFAULT_BUDGET_RANGE);
    setSelectedTypeOfPlace([10, 11, 6]);

    getSquareFeetList().then(({ errorCode, result }) => {
      if (errorCode !== 0 || !result) return;

      const baseList = result.map((itm) => ({
        value: itm.Sqft_Value,
        label: formatSqFtLabel(itm.Sqft_Value, itm.Sqft_Value_Text),
      }));

      setSqareFeetMinList([{ value: null, label: "No min" }, ...baseList]);
      setSqareFeetMaxList([{ value: null, label: "No max" }, ...baseList]);

      // Restore square footage values from filter on first load
      if (isFirstSqftLoad.current && filterQuery?.sqrftMin != null) {
        setSquareFootageMinValue({
          value: filterQuery.sqrftMin,
          label: formatSqFtLabel(filterQuery.sqrftMin),
        });
      } else {
        setSquareFootageMinValue({ value: null, label: "No min" });
      }
      if (isFirstSqftLoad.current && filterQuery?.sqrftMax != null) {
        setSquareFootageMaxValue({
          value: filterQuery.sqrftMax,
          label: formatSqFtLabel(filterQuery.sqrftMax),
        });
      } else {
        setSquareFootageMaxValue({ value: null, label: "No max" });
      }
      isFirstSqftLoad.current = false;

      setStartDate(null);
      setEndDate(null);
    });
  }, [adType]);

  useEffect(() => {
    if (adType !== BUY) {
      let type = "";
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10)
      ) {
        type = "es-pr";
      } else if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "pr-land";
      } else if (
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "es-land";
      }
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "all";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(11)) {
        type = "privateroom";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(10)) {
        type = "entirespace";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(6)) {
        type = "land";
      } else {
        setPropertyList([]);
      }
      getPropertyTypes(type)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });

      if (selectedTypeOfPlace.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }
    }
  }, [selectedTypeOfPlace]);

  // Ref to track latest slider value for onThumbDragEnd
  const budgetRangeRef = useRef(budgetRange);

  const handleSliderInput = (value) => {
    setBudgetRange(value);
    budgetRangeRef.current = value;
  };

  // Only trigger filter when user releases the slider thumb, with a small delay
  const sliderDebounceRef = useRef(null);
  const handleSliderDragEnd = () => {
    if (sliderDebounceRef.current) clearTimeout(sliderDebounceRef.current);
    sliderDebounceRef.current = setTimeout(() => {
      const range = budgetRangeRef.current;
      if (!range) return;
      const maxVal = range[1] === DEFAULT_MAX_BUDGET ? "" : range[1];
      updateFilter({ priceMin: range[0], priceMax: maxVal });
    }, 200);
  };

  useEffect(() => {
    if (adType !== BUY) {
      ///      For loading type of place
      getPropertyTypes("rent")
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setTypeOfPlaceList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [adType]);
  /**
   * To handle clear filter
   */
  const clearFilter = () => {
    setUserLocation(null);
    setLocation("");
    setSearchKeyword("");
    clearFilterQuery();
    setSquareFootageMinValue({ value: null, label: "No min" });
    setSquareFootageMaxValue({ value: null, label: "No max" });
    setBudgetRange([DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET]);
    setPropertyCount(0);
    setTPCount(0);
  };

  /**
   * To handle property selection
   * @param {*} e
   */
  const handlePropertyClick = (e) => {
    const value = convertToNumber(handleRadioButonChange(e));
    const selectedItemArray = getSelectedItems("propertyType");
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      updateFilter({ propertyType: itemArray });
      setPropertyCount(itemArray.length);
    }
  };

  const handleAdditionalRequirementsClick = (e, typeId) => {
    const selectedItemArray = getSelectedItems(e.target.value);
    if (selectedItemArray == 0 || selectedItemArray == null) {
      updateFilter({ [typeId]: 1 });
    } else {
      updateFilter({ [typeId]: 0 });
    }
  };
  /**
   * To handle type of place selection
   * @param {*} e
   */
  const handleTypeOfPlaceClick = (e) => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const value = convertToNumber(handleRadioButonChange(e));
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      setSelectedTypeOfPlace(itemArray);
      if (itemArray.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }

      updateFilter({ typeOfPlace: itemArray });
      setTPCount(itemArray.length);
    }
  };
  /**
   * To load property types
   * @returns
   */
  const loadPropertyMain = () => {
    const selectedItemArray = getSelectedItems("propertyType");
    const List =
      propertyList &&
      propertyList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handlePropertyClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };
  /**
   * To load additional requirements section
   * @returns
   */
  const loadAdditionalRequirements = () => {
    const options = isPrivateRoomSelected
      ? STR_OPTION_DETAILS_PVT_ROOM
      : STR_OPTION_DETAILS_ALL;

    const List =
      options &&
      options.map((pt) => {
        const typeId = _.get(pt, "id", "");
        const typeText = _.get(pt, "text", "");
        const isChecked = getSelectedItems(typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked == 1 ? true : false}
              onChange={(e) => handleAdditionalRequirementsClick(e, typeId)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  /**
   * To load types of place view
   * @returns
   */
  const loadTypeOfPlace = () => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const List =
      typeOfPlaceList &&
      typeOfPlaceList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              name="chkPlace"
              type="checkbox"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handleTypeOfPlaceClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      updateFilter({ search: searchKeyword });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const handleKeyWordSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleBudgetMinChange = (e) => {
    const budgetMax = budgetRange && budgetRange[1];
    const value = handleNegativeInput(e.target.value);
    if (value) {
      setBudgetRange([parseInt(replaceComma(value)), budgetMax]);
    } else {
      setBudgetRange([null, budgetMax]);
    }
  };

  const handleBudgetMaxChange = (e) => {
    const budgetMin = budgetRange && budgetRange[0];
    const value = handleNegativeInput(e.target.value);
    if (parseInt(replaceComma(value))) {
      setBudgetRange([budgetMin, parseInt(replaceComma(value))]);
    } else {
      setBudgetRange([budgetMin, null]);
    }
  };

  // Trigger filter when user finishes editing budget text inputs
  const handleBudgetBlur = () => {
    if (!budgetRange) return;
    const maxVal = budgetRange[1] === DEFAULT_MAX_BUDGET ? "" : budgetRange[1];
    updateFilter({ priceMin: budgetRange[0], priceMax: maxVal });
  };

  const handleSquareFootMinChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMinValue(e);
    updateFilter({ sqrftMin: value });
  };

  const handleSquareFootMaxChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMaxValue(e);
    updateFilter({ sqrftMax: value });
  };

  /**
   * To handle number of bedrooms selection
   * @returns
   */
  const handleBedRoomClick = (e) => {
    const noOfBedrooms = getSelectedItems("bedrooms");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBedrooms) {
      updateFilter({ bedrooms: null });
    } else {
      updateFilter({ bedrooms: selectedValue });
    }
  };
  /**
   * To handle number of beds selection
   * @returns
   */
  const handleBedsClick = (e) => {
    const noOfBeds = getSelectedItems("beds");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBeds) {
      updateFilter({ beds: null });
    } else {
      updateFilter({ beds: selectedValue });
    }
  };
  /**
   * To handle number of bathrooms selection
   * @returns
   */
  const handleBathRoomClick = (e) => {
    const noOfBathrooms = getSelectedItems("baths");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBathrooms) {
      updateFilter({ baths: null });
    } else {
      updateFilter({ baths: selectedValue });
    }
  };

  const addNewOptionsToMinList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMinList(customList);
  };

  const addNewOptionsToMaxList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMaxList(customList);
  };

  const buildCustomList = (list, newOption) => {
    const parseNumeric = (v) =>
      v === null || v === undefined
        ? null
        : Number(String(v).replace(/[^\d.-]/g, ""));

    const formattedList = list.map((item) => {
      if (item.value === null) return item;
      return {
        value: item.value,
        label: formatSqFtLabel(item.value, item.label),
      };
    });

    const value = parseNumeric(_.get(newOption, "value", null));
    if (value === null) return formattedList;

    const exists = formattedList.some(
      (itm) => parseNumeric(itm.value) === value,
    );
    if (!exists) {
      formattedList.push({
        value,
        label: formatSqFtLabel(value),
      });
    }
    return formattedList;
  };

  const handleOnInputChange = (e, dropSection) => {
    if (dropSection === MIN_SF_DROPDOWN) {
      if (minRef.current && minRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMinList(
            arraySplice(squareFeetMinList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMinValue(options);
          updateFilter({ sqrftMin: e });
        }
      }
    } else {
      if (maxRef.current && maxRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMaxList(
            arraySplice(squareFeetMaxList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMaxValue(options);
          updateFilter({ sqrftMax: e });
        }
      }
    }
  };

  /**
   * To handle number of bedrooms selection view
   * @returns
   */
  const loadBedRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("bedrooms");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of beds selection view
   * @returns
   */
  const loadBeds = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("beds");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedsClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of bathrooms selection view
   * @returns
   */
  const loadBathRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBathrooms = getSelectedItems("baths");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBathrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBathRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };

  const updateReloadParamAfterPlanChange = () => {
    loadUserData();
  };

  return (
    <>

      <FilterLocationSearch
        location={location}
        setLocation={setLocation}
        updateFilter={updateFilter}
        adType={adType}
        gitchaNotAvailble={gitchaNotAvailble}
        checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
        additionalLocations={additionalLocations}
        removeLocationAt={removeLocationAt}
      />
      <FilterKeywordSearch
        handleSearch={handleKeyWordSearch}
        keyWord={searchKeyword}
      />

      <Accordion
        defaultActiveKey={gitchaNotAvailble ? null : "2"}
        alwaysOpen
        className="mt-4"
      >
        {adType !== BUY && (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Type of place{" "}
              {getSelectedItems("typeOfPlace").length > 0
                ? `(${getSelectedItems("typeOfPlace").length})`
                : ""}
            </Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">{loadTypeOfPlace()}</div>
            </Accordion.Body>
          </Accordion.Item>
        )}

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            Property type{" "}
            {getSelectedItems("propertyType").length > 0
              ? `(${getSelectedItems("propertyType").length})`
              : ""}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="field-wrap mt-0 mb-0">{loadPropertyMain()}</div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>{getPriceText(adType)}</Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="slider-sec">
              <div className="pb-4 pt-3">
                <RangeSliderComponent
                  min={DEFAULT_MIN_BUDGET}
                  max={DEFAULT_MAX_BUDGET}
                  value={
                    budgetRange && [
                      budgetRange[0] == null
                        ? DEFAULT_MIN_BUDGET
                        : budgetRange[0],
                      budgetRange[1] == null
                        ? DEFAULT_MAX_BUDGET
                        : budgetRange[1],
                    ]
                  }
                  stepValue={DEFAULT_STEP_VALUE}
                  onInput={handleSliderInput}
                  onThumbDragEnd={handleSliderDragEnd}
                />
              </div>
              <ul className="range-values-list rs-pb-37">
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={minError}
                    value={
                      budgetRange && budgetRange[0] === ""
                        ? ""
                        : thousandSeperatedValueFilter(budgetRange[0])
                    }
                    placeholder="0"
                    onChange={(e) => {
                      handleBudgetMinChange(e);
                    }}
                    onBlur={handleBudgetBlur}
                  ></TextBox>
                  <div className="error-msg"></div>
                </li>
                <li className="text-center">
                  <span className="middle-separator">-</span>
                </li>
                <li className="position-relative">
                  <span className="dollar-sign-icon">$</span>
                  <label className="d-none"></label>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={maxError}
                    value={
                      budgetRange && budgetRange[1] === DEFAULT_MAX_BUDGET
                        ? "No max"
                        : thousandSeperatedValue(budgetRange[1])
                    }
                    placeholder="No max"
                    onChange={(e) => {
                      handleBudgetMaxChange(e);
                    }}
                    onBlur={handleBudgetBlur}
                  ></TextBox>
                </li>
              </ul>
            </div>
          </Accordion.Body>
        </Accordion.Item>
        {adType === "STR" && (
          <Accordion.Item eventKey="7">
            <Accordion.Header>Dates</Accordion.Header>
            <Accordion.Body className="pt-0">
              <DateRangeSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}
        <Accordion.Item eventKey="3">
          <Accordion.Header>
            {adType === "STR" ? "Rooms and beds" : "Beds and baths"}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="lp-btn-group-wrap">
              <label className="form-label">
                {adType === "STR" ? "Bedrooms" : "Beds"}
              </label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBedRooms()}
              </div>
            </div>

            {adType === "STR" && isPrivateRoomSelected && (
              <div className="lp-btn-group-wrap">
                <label className="form-label">Beds</label>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  {loadBeds()}
                </div>
              </div>
            )}

            <div className="lp-btn-group-wrap mb-3">
              <label className="form-label">Baths</label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBathRooms()}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {adType !== "STR" && (
          <Accordion.Item eventKey="4">
            <Accordion.Header>Square footage</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="slider-sec">
                <ul className="range-values-list list-pad-fix rs-pb-37">
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={minRef}>
                      <SelectDropDown
                        id={MIN_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMinList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        value={squareFootageMinValue}
                        onChange={(e) => handleSquareFootMinChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MIN_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                  <li className="text-center">
                    <span className="middle-separator">-</span>
                  </li>
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={maxRef}>
                      <SelectDropDown
                        id={MAX_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMaxList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        placeholder="Select"
                        value={squareFootageMaxValue}
                        onChange={(e) => handleSquareFootMaxChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MAX_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
        {adType === "STR" && (
          <Accordion.Item eventKey="6">
            <Accordion.Header>Additional requirements</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">
                {loadAdditionalRequirements()}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>

      <div className="mt-2">
        <a role="button" className="reset-btn" onClick={() => clearFilter()}>
          Clear all
        </a>
      </div>

      <PremiumPlanModal
        userDetails={userDetailsFromHook}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        updateReloadParam={updateReloadParamAfterPlanChange}
        fromInventoryListing={false}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};
export default LeftpanelTabContent;
